import { Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AuthService } from '../../../../services/auth.service';
import { BackOfficeService } from '../../../../services/backoffice.service';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MyConfigOrganization } from '../../../../models/myConfig';
import { TranslocoService } from '@jsverse/transloco';
import { MyConfigService } from '../../../../services/my-config.service';
import { MatTabGroup } from '@angular/material/tabs';

@Component({
  selector: 'app-back-office24-modules',
  templateUrl: './back-office24-modules.component.html',
  styleUrl: './back-office24-modules.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BackOffice24ModulesComponent implements OnInit, OnDestroy {
  componentDestroyed$: Subject<boolean> = new Subject();
  loading = true;
  loadingInitConfig = true;
  @Input() organization: MyConfigOrganization;
  activeLang: string = 'sv';

  allowedTabs: any[] = [];
  selectedTabIndex = 0;

  @ViewChild(MatTabGroup) tabGroup: MatTabGroup;

  constructor(
    public authService: AuthService,
    private backOfficeService: BackOfficeService,
    public router: Router,
    private translocoService: TranslocoService,
    private route: ActivatedRoute,
    private myConfigService: MyConfigService
  ) {
  }

  ngOnInit(): void {
    try {
      this.initConfig();
      this.loadAllowedTabs();

      this.loading = false;
    } catch (e) {
      console.log(e);
    }
    this.activeLang = this.translocoService.getActiveLang();

    this.translocoService.langChanges$.subscribe((lang) => {
      this.activeLang = lang;
    });

    this.router.events.pipe(takeUntil(this.componentDestroyed$)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.updateSelectedTabIndexFromRoute();
        setTimeout(() => {
          this.scrollSelectedTabIntoView();
        }, 0);
      }
    });
    this.updateSelectedTabIndexFromRoute();
    setTimeout(() => {
      this.scrollSelectedTabIntoView();
    }, 0);
  }

  loadAllowedTabs() {
    this.allowedTabs = [
      {
        name: this.organization.organizationName + ' - Start',
        description: 'Start',
        icon: 'home',
        route: '.',
      }
    ];
    this.organization.config.backOfficeModules.forEach(tab => {
      tab.menu.description = JSON.stringify(tab.menu.description);
      tab.menu.name = JSON.stringify(tab.menu.name);

      this.allowedTabs.push({
        ...tab.menu,
      });
    });
  }

  updateSelectedTabIndexFromRoute() {
    const currentUrl = this.router.url;
    const urlWithoutQuery = currentUrl.split('?')[0];
    const matchedTabIndex = this.allowedTabs.findIndex(tab => {
      return urlWithoutQuery.endsWith(`/${tab.route}`)
    });
    this.selectedTabIndex = matchedTabIndex >= 0 ? matchedTabIndex : 0;
  }

  onTabChange(index: number) {
    const tab = this.allowedTabs[index];
    const currentUrlWithoutQuery = this.router.url.split('?')[0];
    if (currentUrlWithoutQuery.endsWith(`/${tab.route}`)) {
      return;
    }
    this.router.navigate([tab.route], { relativeTo: this.route });
  }

  private scrollSelectedTabIntoView() {
    if (this.tabGroup?._tabHeader?._scrollToLabel) {
      this.tabGroup._tabHeader._scrollToLabel(this.selectedTabIndex);
    }
  }

  initConfig() {
    if (!this.myConfigService.myConfig || !this.myConfigService.myConfig.organizations) {
      console.error('myConfig is undefined or does not have organizations');
      return;
    }

    this.route.params.pipe(takeUntil(this.componentDestroyed$)).subscribe(params => {
      this.myConfigService.myConfig.organizations.forEach(org => {
        if (org.config?.backOfficeModules) {
          org.config.backOfficeModules.forEach(module => {
            if (module.menu && module.menu.name && module.menu.description) {
              try {
                if (typeof module.menu.name === 'string') {
                  module.menu.name = JSON.parse(module.menu.name);
                }
                if (typeof module.menu.description === 'string') {
                  module.menu.description = JSON.parse(module.menu.description);
                }
              } catch (error) {
                console.error('Error parsing JSON:', error, module.menu);
              }
            }
          });
        }
      });
    });

  }

  getTranslatedLabel(moduleName: string): string {
    const backOfficeModules = this.myConfigService.myConfig.organizations.flatMap(org =>
      org.config?.backOfficeModules || []
    );

    const module = backOfficeModules.find(m =>
      m.name === moduleName
    );

    return module ? module.menu?.name[this.activeLang] : moduleName;
  }

  ngOnDestroy(): void {
    // throw new Error('Method not implemented.');
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

}
